<template>
  <div class="meeting-layout">
    <!-- Embedded Meeting Sidebar on the left -->
    <EmbeddedMeetingSidebar
      v-if="meeting.active || isMeetingOngoing"
      :sid="meeting.sid || session.sid"
      :userName="meeting.name || user.nickname || user.name || 'Teacher'"
      :uid="user._id || ''"
      :isTeacher="true"
      :audioMuted="!!meeting.audioMuted"
      :videoMuted="!!meeting.videoMuted"
      roomName="MainRoom"
      class="meeting-sidebar"
      :class="{ 'is-collapsed': isSidebarCollapsed }"
      @left="onMeetingLeft"
      @error="onMeetingLeft"
      @collapse="onCollapse"
      @accepted-student-help="onAcceptedStudentHelp"
      @left-student-help="onLeftStudentHelp"
      @channel-students-updated="onChannelStudentsUpdated"
    />

    <!-- Main Content Area where the actual page (teacherPage) will be rendered -->
    <div class="main-content-area" :class="{ 'has-sidebar': meeting.active || isMeetingOngoing }">
      <!-- We render the matched child route here -->
      <router-view :key="$route.fullPath"></router-view>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import EmbeddedMeetingSidebar from '@/components/EmbeddedMeetingSidebar.vue'

export default {
  name: 'MeetingLayout',
  components: {
    EmbeddedMeetingSidebar
  },
  data() {
    return {
      isSidebarCollapsed: false
    }
  },
  computed: {
    ...mapState(['session', 'meeting', 'user']),
    isMeetingOngoing() {
      if (this.session && this.session.lastMeetingHeartbeat) {
        return Date.now() - this.session.lastMeetingHeartbeat < 30000
      }
      return false
    },
    showSidebar() {
      return this.meeting.active || this.isMeetingOngoing
    }
  },
  methods: {
    onMeetingLeft() {
      this.$store.commit('setMeeting', { 
        active: false, 
        minimized: false, 
        name: '', 
        sid: '', 
        audioMuted: true, 
        videoMuted: true 
      })
    },
    onCollapse(val) {
      this.isSidebarCollapsed = val
    },
    onAcceptedStudentHelp(data) {
      if (window.EventBus) {
        window.EventBus.$emit('accepted-student-help', data)
      }
    },
    onLeftStudentHelp(data) {
      if (window.EventBus) {
        window.EventBus.$emit('left-student-help', data)
      }
    },
    onChannelStudentsUpdated(data) {
      if (window.EventBus) {
        window.EventBus.$emit('channel-students-updated', data)
      }
    }
  },
  watch: {
    showSidebar: {
      handler(val) {
        if (val) {
          document.body.classList.add('meeting-sidebar-active')
        } else {
          document.body.classList.remove('meeting-sidebar-active')
        }
      },
      immediate: true
    },
    isSidebarCollapsed: {
      handler(val) {
        if (val) {
          document.body.classList.add('meeting-sidebar-collapsed')
        } else {
          document.body.classList.remove('meeting-sidebar-collapsed')
        }
      },
      immediate: true
    }
  },
  destroyed() {
    document.body.classList.remove('meeting-sidebar-active')
    document.body.classList.remove('meeting-sidebar-collapsed')
  }
}
</script>

<style>
/* Global styles to shift fixed modals when sidebar is active */
body.meeting-sidebar-active .el-dialog__wrapper,
body.meeting-sidebar-active .el-drawer__wrapper,
body.meeting-sidebar-active .v-modal,
body.meeting-sidebar-active .gt-main-wrapper,
body.meeting-sidebar-active .ga-overlay,
body.meeting-sidebar-active .gs-overlay,
body.meeting-sidebar-active .team-modal-overlay,
body.meeting-sidebar-active .team-edit-overlay,
body.meeting-sidebar-active .tm-submodal-overlay,
body.meeting-sidebar-active .gs-preview-overlay,
body.meeting-sidebar-active .sa-overlay,
body.meeting-sidebar-active .go-overlay,
body.meeting-sidebar-active .cw-overlay,
body.meeting-sidebar-active .wae-review-overlay,
body.meeting-sidebar-active .ds-overlay,
body.meeting-sidebar-active .cpadp-overlay,
body.meeting-sidebar-active .rt-overlay,
body.meeting-sidebar-active .npp-overlay,
body.meeting-sidebar-active .video-quiz-overlay,
body.meeting-sidebar-active .control {
  left: 280px !important;
  right: 0 !important;
  width: calc(100% - 280px) !important;
  max-width: calc(100% - 280px) !important;
  transition: all 0.3s ease;
}

body.meeting-sidebar-active .el-dialog {
  max-width: calc(100% - 280px) !important;
}

body.meeting-sidebar-active .popup-overlay {
  left: calc(50% + 140px) !important;
  transition: all 0.3s ease;
}

/* Collapsed overrides */
body.meeting-sidebar-active.meeting-sidebar-collapsed .el-dialog__wrapper,
body.meeting-sidebar-active.meeting-sidebar-collapsed .el-drawer__wrapper,
body.meeting-sidebar-active.meeting-sidebar-collapsed .v-modal,
body.meeting-sidebar-active.meeting-sidebar-collapsed .gt-main-wrapper,
body.meeting-sidebar-active.meeting-sidebar-collapsed .ga-overlay,
body.meeting-sidebar-active.meeting-sidebar-collapsed .gs-overlay,
body.meeting-sidebar-active.meeting-sidebar-collapsed .team-modal-overlay,
body.meeting-sidebar-active.meeting-sidebar-collapsed .team-edit-overlay,
body.meeting-sidebar-active.meeting-sidebar-collapsed .tm-submodal-overlay,
body.meeting-sidebar-active.meeting-sidebar-collapsed .gs-preview-overlay,
body.meeting-sidebar-active.meeting-sidebar-collapsed .sa-overlay,
body.meeting-sidebar-active.meeting-sidebar-collapsed .go-overlay,
body.meeting-sidebar-active.meeting-sidebar-collapsed .cw-overlay,
body.meeting-sidebar-active.meeting-sidebar-collapsed .wae-review-overlay,
body.meeting-sidebar-active.meeting-sidebar-collapsed .ds-overlay,
body.meeting-sidebar-active.meeting-sidebar-collapsed .cpadp-overlay,
body.meeting-sidebar-active.meeting-sidebar-collapsed .rt-overlay,
body.meeting-sidebar-active.meeting-sidebar-collapsed .npp-overlay,
body.meeting-sidebar-active.meeting-sidebar-collapsed .video-quiz-overlay,
body.meeting-sidebar-active.meeting-sidebar-collapsed .control {
  left: 140px !important;
  width: calc(100% - 140px) !important;
  max-width: calc(100% - 140px) !important;
}

body.meeting-sidebar-active.meeting-sidebar-collapsed .el-dialog {
  max-width: calc(100% - 140px) !important;
}

body.meeting-sidebar-active.meeting-sidebar-collapsed .popup-overlay {
  left: calc(50% + 70px) !important;
}
</style>

<style scoped>
.meeting-layout {
  display: flex;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background-color: #f1f5f9;
}

.meeting-sidebar {
  width: 280px;
  height: 100vh;
  flex-shrink: 0;
  z-index: 2000000000; 
  transition: width 0.3s ease;
}
.meeting-sidebar.is-collapsed {
  width: 140px;
}

.main-content-area {
  flex-grow: 1;
  height: 100vh;
  overflow: hidden;
  position: relative;
}
</style>
